import {useRef, useState} from 'react';

import {isNil} from 'ramda';

import {useOnMount} from 'shared/web';

import type {
  BulkAction,
  BulkOperationResponseBody,
  DataGridBulkActionsResponseBody,
  SubmitBulkOperationResponseBody,
  SubmitFilteredRecordsBulkOperationRequestBody,
} from '../../../types/api/bulkOperations';
import type {
  BulkOperationActionCallback,
  BulkOperationActionContext,
  BulkOperationSelectionSource,
} from '../../../types/BulkOperationActionCallback';
import type {BulkOperationActionDefinitions} from '../../../types/BulkOperationActionDefinition';
import type {BulkOperationsServices} from '../../../types/BulkOperationsServices';
import {createBulkOperationActionContext} from '../../../utils/createBulkOperationActionContext';
import {createPendingBulkOperation} from '../../../utils/createPendingBulkOperation';
import {getBulkOperationActionDefinition} from '../../../utils/getBulkOperationActionDefinition';

type PendingBulkOperationAction = {id: number; invoke: () => void};

/**
 * Owns the action-context session: at most one open context, closed on unmount.
 * Dispatch is deferred through the returned `pendingAction` so managed dialogs
 * survive the URL update that opening an action may trigger.
 */
export function useBulkOperationActionDispatch(args: {
  discovery: DataGridBulkActionsResponseBody | undefined;
  actionsDefinition: BulkOperationActionDefinitions;
  actionCallback?: BulkOperationActionCallback;
  selectionSource: BulkOperationSelectionSource;
  services: BulkOperationsServices;
  gridCode: string;
  presetInstanceId?: string;
  submitFiltered: (
    body: SubmitFilteredRecordsBulkOperationRequestBody
  ) => Promise<SubmitBulkOperationResponseBody>;
  /** Runs before a new action context is created, e.g. to clear a linked operation. */
  onOpen: () => void;
  /** Receives the accepted operation's immediate pending representation. */
  onSubmitted: (operation: BulkOperationResponseBody) => void;
  testId?: string;
}) {
  const activeActionContext = useRef<BulkOperationActionContext | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingBulkOperationAction | null>(null);
  const [submitSelected] = args.services.useSubmitSelected();
  useOnMount(
    () => undefined,
    () => activeActionContext.current?.close()
  );

  const openSubmitDialog = (action: BulkAction) => {
    const discovery = args.discovery;
    if (isNil(args.actionCallback) || isNil(discovery)) {
      return;
    }
    if (
      !isNil(activeActionContext.current) &&
      !activeActionContext.current.getSnapshot().isClosed
    ) {
      return;
    }
    args.onOpen();
    const context = createBulkOperationActionContext({
      source: args.selectionSource,
      submit: (target, params) => {
        const request =
          target.type === 'selected'
            ? submitSelected({
                resourceId: discovery.resourceId,
                actionCode: action.actionCode,
                body: {recordIds: target.recordIds, params},
              }).unwrap()
            : args.submitFiltered({
                actionCode: action.actionCode,
                dataQueryId: target.dataQueryId,
                params,
              });
        return request.then((response) => {
          if (!context.getSnapshot().isClosed) {
            args.onSubmitted(
              createPendingBulkOperation({
                actionCode: action.actionCode,
                bulkOperationId: response.bulkOperationId,
                resourceId: discovery.resourceId,
                totalCount:
                  target.type === 'selected' ? target.recordIds.length : target.totalCount,
              })
            );
          }
        });
      },
    });
    activeActionContext.current = context;
    const invocation = {
      actionKey: action.actionCode,
      resourceId: discovery.resourceId,
      gridCode: args.gridCode,
      presetInstanceId: args.presetInstanceId,
      actionDefinition: getBulkOperationActionDefinition(action.actionCode, args.actionsDefinition),
      paramsSchema: action.paramsSchema,
      context,
      'data-testid': args.testId,
    };
    setPendingAction((current) => ({
      id: (current?.id ?? 0) + 1,
      invoke: () => {
        void context
          .dispatch(() => args.actionCallback?.(invocation))
          .catch(args.services.handleApiError ?? console.error);
      },
    }));
  };

  return {pendingAction, openSubmitDialog};
}
